import type { ClientVM } from "./view-model";
import type { TierRank } from "../components/tier/tier";

/**
 * Tier + timeline-bucket filter predicates for the Compose audience builder
 * (`lib/audience.ts`) and the Recipients panel (`components/compose/
 * RecipientPanel.tsx`). Relocated out of the retired `components/clients/
 * client-filters.ts` (person-model-completion M4, design §6.3) — the
 * Clients-page-only exports it also carried (`OpTabId`, `OP_TABS`,
 * `attentionScore`, `sortClients`, `tabCounts`, `unverifiedCount`) had no
 * consumer left once `ClientListView`/`FilterBar`/`ClientsToolbar` were
 * deleted (superseded by `PeopleRoster`) and were dropped rather than moved.
 */

/**
 * Timeline bucket ids derived from `ClientVM.timelineShort`.
 * "any" passes every client regardless of timeline.
 * The four named buckets match the spec text verbatim (en-dashes, not hyphens).
 */
export type TimelineBucketId =
  | "any"
  | "0–3 mo"
  | "3–6 mo"
  | "6–12 mo"
  | "12+ mo";

export interface TimelineBucketDef {
  id: TimelineBucketId;
  label: string;
}

/**
 * Ordered bucket definitions for the timeline-bucket dimension.
 * "any" is always first; the four named buckets follow in ascending order.
 */
export const TIMELINE_BUCKETS: TimelineBucketDef[] = [
  { id: "any", label: "Any timeline" },
  { id: "0–3 mo", label: "0–3 mo" },
  { id: "3–6 mo", label: "3–6 mo" },
  { id: "6–12 mo", label: "6–12 mo" },
  { id: "12+ mo", label: "12+ mo" },
];

/**
 * Maps a free-text horizon ("0–3 months", "3-6 months", the onboarding token
 * "6_12_months", "12+ months") or a timeline value ("0–3 mo", "0-3mo") onto
 * one bucket, or "" when it names none. Deliberately literal: "12 months" or
 * "10-30 days" name no bucket. Mirrors the backend's crm.TimelineBucket, and
 * backend/internal/services/crm/testdata/audience_preset_fixture.json pins the
 * two to the same answers.
 */
export function timelineBucket(horizon?: string | null): Exclude<TimelineBucketId, "any"> | "" {
  const s = (horizon ?? "")
    .trim()
    .toLowerCase()
    .replace(/[–—_]/g, "-");
  const range = /(\d+)\s*-\s*(\d+)/.exec(s);
  if (range) {
    switch (`${range[1]}-${range[2]}`) {
      case "0-3":
        return "0–3 mo";
      case "3-6":
        return "3–6 mo";
      case "6-12":
        return "6–12 mo";
      default:
        return "";
    }
  }
  const plus = /(\d+)\s*(?:\+|-?\s*plus\b)/.exec(s);
  if (plus && Number(plus[1]) === 12) return "12+ mo";
  return "";
}

/**
 * False when a client's verification tier is hidden (financials locked or
 * unavailable): its `tier` then means nothing, and no tier preset or filter
 * may match it. Absent state means available.
 */
export function isTierKnown(c: Pick<ClientVM, "enrichmentState">): boolean {
  return c.enrichmentState === undefined || c.enrichmentState === "available";
}

/**
 * Returns true when the client's `timelineShort` matches the given bucket,
 * or when the bucket is "any" (passes everything).
 *
 * The match is an exact string equality against `ClientVM.timelineShort`
 * because the server encodes the discrete bucket in that field directly.
 */
export function passesTimelineFilter(
  c: ClientVM,
  filter: TimelineBucketId,
): boolean {
  if (filter === "any") return true;
  return c.timelineShort === filter;
}

/** Tier filter chip ids. "any" passes everything. */
export type TierFilterId = "any" | "bank" | "doc";

const TIER_FILTER_RANK: Record<Exclude<TierFilterId, "any">, TierRank> = {
  bank: 3,
  doc: 2,
};

export function passesTierFilter(c: ClientVM, filter: TierFilterId): boolean {
  if (filter === "any") return true;
  return isTierKnown(c) && c.tier === TIER_FILTER_RANK[filter];
}
